import {
  SERVICE_TOPOLOGY,
  ServiceStatus,
  type ArchitectureHealth,
  type ServiceNodeId,
  type ServiceStatus as ServiceStatusT,
} from '@ddp/shared';

/**
 * Schéma d'état de l'architecture (ADR-0037 §5) : SVG **maison**, pas de dépendance type ReactFlow
 * (budget de bundle, NFR-11). Purement présentiel — les données (état de chaque bloc) sont fournies
 * par le parent, qui les rafraîchit par interrogation périodique. La topologie (blocs + liens) vient
 * du contrat partagé `SERVICE_TOPOLOGY`, source unique commune à l'API et au schéma.
 */

/** Libellés d'état en français (identifiants d'énum en anglais, affichage en français). */
export const STATUS_LABEL_FR: Record<ServiceStatusT, string> = {
  [ServiceStatus.OK]: 'OK',
  [ServiceStatus.WARN]: 'Alerte mineure',
  [ServiceStatus.IMMINENT]: 'Problème imminent',
  [ServiceStatus.DOWN]: 'Indisponible',
  [ServiceStatus.DORMANT]: 'Dormant',
};

/** Suffixe de classe CSS par état — pilote la couleur (variables définies dans app.scss). */
const STATUS_CLASS: Record<ServiceStatusT, string> = {
  [ServiceStatus.OK]: 'ok',
  [ServiceStatus.WARN]: 'warn',
  [ServiceStatus.IMMINENT]: 'imminent',
  [ServiceStatus.DOWN]: 'down',
  [ServiceStatus.DORMANT]: 'dormant',
};

/** Ordre de la légende : du plus sain au plus grave, puis l'état neutre. */
const LEGEND_ORDER: ServiceStatusT[] = [
  ServiceStatus.OK,
  ServiceStatus.WARN,
  ServiceStatus.IMMINENT,
  ServiceStatus.DOWN,
  ServiceStatus.DORMANT,
];

/** Position du centre de chaque bloc dans le `viewBox` (unités SVG). Mise en page en couches. */
const POSITIONS: Record<ServiceNodeId, { x: number; y: number }> = {
  caddy: { x: 350, y: 42 },
  web: { x: 210, y: 150 },
  api: { x: 470, y: 150 },
  worker: { x: 470, y: 258 },
  'external-gpu': { x: 630, y: 258 },
  postgres: { x: 210, y: 366 },
  valkey: { x: 360, y: 366 },
  garage: { x: 510, y: 366 },
};

const NODE_W = 128;
const NODE_H = 48;

export interface ResolvedNode {
  id: ServiceNodeId;
  label: string;
  status: ServiceStatusT;
  detail?: string;
  external: boolean;
  x: number;
  y: number;
}

/**
 * Fusionne la topologie (blocs) avec l'état mesuré. Un bloc absent du payload retombe sur `DORMANT`
 * (neutre) plutôt que sur un faux « OK » : on n'invente pas un état sain qu'on n'a pas mesuré. Pur
 * et testable (la donnée pilote l'affichage).
 */
export function resolveNodes(health: ArchitectureHealth | null): ResolvedNode[] {
  const byId = new Map(health?.services.map((s) => [s.id, s]) ?? []);
  return SERVICE_TOPOLOGY.nodes.map((node) => {
    const measured = byId.get(node.id);
    const pos = POSITIONS[node.id];
    return {
      id: node.id,
      label: node.label,
      status: measured?.status ?? ServiceStatus.DORMANT,
      detail: measured?.detail,
      external: node.external ?? false,
      x: pos.x,
      y: pos.y,
    };
  });
}

export function ArchitectureSchema({ health }: { health: ArchitectureHealth | null }) {
  const nodes = resolveNodes(health);
  const byId = new Map(nodes.map((n) => [n.id, n]));

  return (
    <div className="ddp-arch">
      <svg
        className="ddp-arch__svg"
        viewBox="0 0 700 416"
        role="img"
        aria-label="Schéma des services de l’architecture et de leur état"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Liens d'abord, sous les blocs : ils décrivent le sens du flux (ADR-0037 §5). */}
        <g className="ddp-arch__links">
          {SERVICE_TOPOLOGY.links.map((link) => {
            const from = byId.get(link.from);
            const to = byId.get(link.to);
            if (!from || !to) return null;
            return (
              <line
                key={`${link.from}-${link.to}`}
                x1={from.x}
                y1={from.y + NODE_H / 2}
                x2={to.x}
                y2={to.y - NODE_H / 2}
                className="ddp-arch__link"
              />
            );
          })}
        </g>

        {nodes.map((node) => (
          <g
            key={node.id}
            className={`ddp-arch__node is-${STATUS_CLASS[node.status]}${node.external ? ' is-external' : ''}`}
            transform={`translate(${node.x - NODE_W / 2}, ${node.y - NODE_H / 2})`}
          >
            <rect className="ddp-arch__box" width={NODE_W} height={NODE_H} rx={6} ry={6} />
            <circle className="ddp-arch__dot" cx={16} cy={NODE_H / 2} r={6} />
            <text className="ddp-arch__label" x={30} y={NODE_H / 2 - 2}>
              {node.label}
            </text>
            <text className="ddp-arch__status" x={30} y={NODE_H / 2 + 13}>
              {STATUS_LABEL_FR[node.status]}
            </text>
            {node.detail && <title>{`${node.label} — ${node.detail}`}</title>}
          </g>
        ))}
      </svg>

      <ul className="ddp-arch__legend" aria-hidden="true">
        {LEGEND_ORDER.map((status) => (
          <li key={status} className={`ddp-arch__legend-item is-${STATUS_CLASS[status]}`}>
            <span className="ddp-arch__legend-swatch" />
            {STATUS_LABEL_FR[status]}
          </li>
        ))}
      </ul>

      {/* Doublure textuelle pour les lecteurs d'écran : le SVG seul ne porte pas l'état par bloc. */}
      <ul className="ddp-visually-hidden">
        {nodes.map((node) => (
          <li key={node.id}>
            {node.label} : {STATUS_LABEL_FR[node.status]}
            {node.detail ? ` (${node.detail})` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}
